import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CrossIcon, FilterIcon, SearchIcon } from '../../helpers/icons';
import { Spinner } from '../Loaders';

const PANEL_WIDTH = 252;
// One request per scroll, matching the server's own page size. The list shows
// about six rows at a time, so a page is three screens of scrolling.
const VALUES_PAGE_SIZE = 20;
// How close to the bottom of the list counts as "at the bottom": far enough
// that the next page is usually already there when the user reaches it.
const VALUES_SCROLL_THRESHOLD = 48;
const PANEL_GAP = 4;
// Roughly the head, footer and a short list. Used only to decide whether the
// panel opens downwards or flips above its header.
const PANEL_MIN_HEIGHT = 260;

const TEXT_OPERATORS = [
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does not equal' }, 
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'blank', label: 'Is blank' },
  { value: 'not_blank', label: 'Is not blank' },
];

const NUMBER_OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'ne', label: 'Does not equal' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater or equal' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less or equal' },
  { value: 'between', label: 'Between' },
];

// A text or number filter needs no value once the operator is "is blank", so
// "does this actually narrow anything" is not simply "is a value set".
const isMeaningful = (draft) => {
  if (!draft) return false;
  if (draft.type === 'values') {
    return draft.values?.length > 0 || draft.includeBlank;
  }
  if (draft.type === 'text') {
    if (draft.operator === 'blank' || draft.operator === 'not_blank') {
      return true;
    }
    return String(draft.value ?? '').trim().length > 0;
  }
  if (draft.type === 'number') {
    if (draft.operator === 'between') {
      return (
        String(draft.from ?? '').trim() !== '' &&
        String(draft.to ?? '').trim() !== ''
      );
    }
    return String(draft.value ?? '').trim().length > 0;
  }
  if (draft.type === 'date') {
    return Boolean(draft.from || draft.to);
  }
  return false;
};

const emptyDraft = (type) => {
  if (type === 'text') return { type: 'text', operator: 'contains', value: '' };
  if (type === 'number') {
    return { type: 'number', operator: 'eq', value: '', from: '', to: '' };
  }
  if (type === 'date') return { type: 'date', from: '', to: '' };
  return { type: 'values', values: [], includeBlank: false };
};

// The checkbox list: the column's distinct values across the whole report, not
// just the page on screen, so ticking one can pull in rows from any page.
const ValuesFilter = ({
  column,
  valuesFn,
  valuesQueryKey,
  context,
  draft,
  setDraft,
}) => {
  const [search, setSearch] = useState('');
  // The list is re-fetched per settled search term rather than filtered
  // locally, because the client only ever holds the pages it has scrolled
  // through, not the column's whole set of values.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [valuesQueryKey, column, debouncedSearch, context],
    queryFn: ({ pageParam = 1 }) =>
      valuesFn({
        ...context,
        column,
        search: debouncedSearch || '',
        // Under their own keys, because `page` on this payload is the listing's
        // own -- these values are scoped by that listing, not paged with it.
        values_page: pageParam,
        values_limit: VALUES_PAGE_SIZE,
      }),
    initialPageParam: 1,
    // The server answers "is there another page" rather than a total, so no
    // COUNT(DISTINCT) is paid for a list the user may never scroll.
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.data?.data?.hasMore ? allPages.length + 1 : undefined,
    enabled: Boolean(valuesFn),
    // Keeps the current list on screen while a new search term is in flight, so
    // the panel does not collapse and re-expand on every keystroke.
    placeholderData: keepPreviousData,
  });

  // Two hops, like every other list in this table: axios hands back the
  // response, whose `data` is the API envelope, whose own `data` is the
  // controller's payload.
  const pages = data?.pages ?? [];
  // Only the first page can carry blanks: NULL and "" sort before every real
  // value, which is also why "(Blanks)" stays pinned above the list.
  const payload = pages[0]?.data?.data ?? {};
  const values = useMemo(
    () =>
      (data?.pages ?? []).flatMap((page) => {
        const list = page?.data?.data?.values;
        return Array.isArray(list) ? list : [];
      }),
    [data],
  );
  const selected = useMemo(
    () => new Set((draft.values || []).map((value) => String(value))),
    [draft.values],
  );

  // "Select all" ticks the values that have actually been loaded -- the same
  // thing it means in Excel once a search has narrowed the list.
  const allTicked =
    values.length > 0 &&
    values.every((value) => selected.has(String(value))) &&
    (!payload.hasBlank || draft.includeBlank);

  const handleScroll = (event) => {
    if (!hasNextPage || isFetchingNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= VALUES_SCROLL_THRESHOLD) {
      fetchNextPage();
    }
  };

  const toggleValue = (value) => {
    const key = String(value);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setDraft({ ...draft, values: [...next] });
  };

  const toggleAll = () => {
    if (allTicked) {
      setDraft({ ...draft, values: [], includeBlank: false });
      return;
    }
    setDraft({
      ...draft,
      values: values.map((value) => String(value)),
      includeBlank: Boolean(payload.hasBlank),
    });
  };

  return (
    <>
      <div className="column-filter__search">
        <SearchIcon width={13} height={13} />
        <input
          type="text"
          placeholder="Search values"
          value={search}
          autoFocus
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="column-filter__loader">
          <Spinner size="sm" />
        </div>
      ) : values.length === 0 && !payload.hasBlank ? (
        <div className="column-filter__empty">No matching values</div>
      ) : (
        <div
          className="column-filter__list"
          onScroll={handleScroll}
          // Dimmed while a new search term is in flight, but not while the next
          // page is loading: that would flicker the whole list on every scroll.
          style={{
            opacity: isFetching && !isFetchingNextPage ? 0.55 : 1,
          }}
        >
          <label className="column-filter__option column-filter__option--all">
            <input type="checkbox" checked={allTicked} onChange={toggleAll} />
            <span>Select all</span>
          </label>
          {payload.hasBlank && (
            <label className="column-filter__option column-filter__option--blank">
              <input
                type="checkbox"
                checked={Boolean(draft.includeBlank)}
                onChange={() =>
                  setDraft({ ...draft, includeBlank: !draft.includeBlank })
                }
              />
              <span>(Blanks)</span>
            </label>
          )}
          {values.map((value) => (
            <label
              key={String(value)}
              className="column-filter__option"
              title={String(value)}
            >
              <input
                type="checkbox"
                checked={selected.has(String(value))}
                onChange={() => toggleValue(value)}
              />
              <span>{String(value)}</span>
            </label>
          ))}
          {isFetchingNextPage && (
            <div className="column-filter__loader column-filter__loader--more">
              <Spinner size="sm" />
            </div>
          )}
        </div>
      )}

      {!isLoading && hasNextPage && !isFetchingNextPage && (
        <div className="column-filter__note">
          Scroll for more &mdash; or search to narrow the list.
        </div>
      )}
    </>
  );
};

const TextFilter = ({ draft, setDraft }) => {
  const needsValue =
    draft.operator !== 'blank' && draft.operator !== 'not_blank';

  return (
    <div className="column-filter__field">
      <select
        value={draft.operator}
        onChange={(event) =>
          setDraft({ ...draft, operator: event.target.value })
        }
      >
        {TEXT_OPERATORS.map((operator) => (
          <option key={operator.value} value={operator.value}>
            {operator.label}
          </option>
        ))}
      </select>
      {needsValue && (
        <input
          type="text"
          placeholder="Value"
          value={draft.value ?? ''}
          autoFocus
          onChange={(event) =>
            setDraft({ ...draft, value: event.target.value })
          }
        />
      )}
    </div>
  );
};

const NumberFilter = ({ draft, setDraft }) => (
  <div className="column-filter__field">
    <select
      value={draft.operator}
      onChange={(event) => setDraft({ ...draft, operator: event.target.value })}
    >
      {NUMBER_OPERATORS.map((operator) => (
        <option key={operator.value} value={operator.value}>
          {operator.label}
        </option>
      ))}
    </select>
    {draft.operator === 'between' ? (
      <div className="column-filter__row">
        <input
          type="number"
          placeholder="From"
          value={draft.from ?? ''}
          onChange={(event) => setDraft({ ...draft, from: event.target.value })}
        />
        <input
          type="number"
          placeholder="To"
          value={draft.to ?? ''}
          onChange={(event) => setDraft({ ...draft, to: event.target.value })}
        />
      </div>
    ) : (
      <input
        type="number"
        placeholder="Value"
        value={draft.value ?? ''}
        autoFocus
        onChange={(event) => setDraft({ ...draft, value: event.target.value })}
      />
    )}
  </div>
);

const DateFilter = ({ draft, setDraft }) => (
  <div className="column-filter__field">
    <span className="column-filter__label">From</span>
    <input
      type="date"
      value={draft.from ?? ''}
      max={draft.to || undefined}
      onChange={(event) => setDraft({ ...draft, from: event.target.value })}
    />
    <span className="column-filter__label">To</span>
    <input
      type="date"
      value={draft.to ?? ''}
      min={draft.from || undefined}
      onChange={(event) => setDraft({ ...draft, to: event.target.value })}
    />
  </div>
);

/**
 * The filter control on a table header: a funnel that opens an Excel-style
 * panel for that column. Sorting stays on the header's own arrow, beside it.
 * Filters are applied on the server, so they narrow the whole report rather
 * than the rows currently on screen.
 */
export const ColumnFilter = ({
  column,
  label,
  config = {},
  value = null,
  onChange = () => {},
  valuesFn,
  valuesQueryKey = 'columnFilterValues',
  context = {},
}) => {
  const { type = 'values' } = config;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => value ?? emptyDraft(type));
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const isActive = isMeaningful(value);

  // The panel is portalled to the body, because a `<th>` sits inside the
  // table's own scroll container, which would clip it.
  useLayoutEffect(() => {
    if (!open) return undefined;

    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();

      // The last headers of a wide table are exactly where this opens most
      // often, and there the panel would otherwise hang off the right edge.
      const left = Math.min(
        Math.max(8, rect.left),
        Math.max(8, window.innerWidth - PANEL_WIDTH - 8),
      );
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < PANEL_MIN_HEIGHT && rect.top > spaceBelow;

      setPosition(
        openUp
          ? {
              left,
              bottom: window.innerHeight - rect.top + PANEL_GAP,
              maxHeight: rect.top - PANEL_GAP - 8,
            }
          : {
              left,
              top: rect.bottom + PANEL_GAP,
              maxHeight: spaceBelow - PANEL_GAP - 8,
            },
      );
    };

    place();
    // A fixed-position panel goes stale the moment the table scrolls under it,
    // so it tracks its own header instead of drifting away from it.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (
        panelRef.current?.contains(event.target) ||
        triggerRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const toggleOpen = (event) => {
    // The header cell itself toggles sorting, which a click on this control is
    // not asking for.
    event.stopPropagation();
    setDraft(value ?? emptyDraft(type));
    setOpen((previous) => !previous);
  };

  const apply = () => {
    onChange(isMeaningful(draft) ? { ...draft, column, type } : null);
    setOpen(false);
  };

  const clear = () => {
    setDraft(emptyDraft(type));
    onChange(null);
    setOpen(false);
  };

  const selectedCount =
    type === 'values'
      ? (draft.values?.length || 0) + (draft.includeBlank ? 1 : 0)
      : 0;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`column-filter__trigger ${
          isActive ? 'column-filter__trigger--active' : ''
        }`}
        onClick={toggleOpen}
      >
        {/* A funnel either way, so the control reads as "filter" before it has
            one: outlined when the column is unfiltered, solid once it is. */}
        <FilterIcon width={11} height={11} isActive={isActive} />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            className="column-filter__panel"
            style={{ ...position, width: PANEL_WIDTH }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="column-filter__head">
              <span className="column-filter__title" title={label}>
                {label}
              </span>
              <button
                type="button"
                className="column-filter__close"
                onClick={() => setOpen(false)}
              >
                <CrossIcon width={11} height={11} />
              </button>
            </div>

            <div className="column-filter__section column-filter__section--body">
              {type === 'values' && (
                <ValuesFilter
                  {...{
                    column,
                    valuesFn,
                    valuesQueryKey,
                    context,
                    draft,
                    setDraft,
                  }}
                />
              )}
              {type === 'text' && <TextFilter {...{ draft, setDraft }} />}
              {type === 'number' && <NumberFilter {...{ draft, setDraft }} />}
              {type === 'date' && <DateFilter {...{ draft, setDraft }} />}
            </div>

            <div className="column-filter__foot">
              <span className="column-filter__count">
                {selectedCount > 0 ? `${selectedCount} selected` : ''}
              </span>
              <div className="column-filter__actions">
                <button
                  type="button"
                  className="column-filter__btn column-filter__btn--ghost"
                  onClick={clear}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="column-filter__btn column-filter__btn--primary"
                  onClick={apply}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export { isMeaningful };
